/**
 * Back (and forward) puts each list back where it was scrolled. The lists
 * scroll inside their own pane (`.list-scroll`), not the window, so the
 * router's scrollBehavior never sees them; and a page left for another
 * (Ledger → Library) is unmounted, so its pane starts again at the top.
 *
 * Each list's scroll offset is kept per history entry (vue-router's
 * `history.state.position`) and per list. A navigation that keeps the list
 * on screen (opening an item beside it) carries the offset to the new entry,
 * so going back to that entry finds it too. A back or forward restores the
 * entry's offset — once the rows are there to scroll to. A new entry (push)
 * starts empty, so following a link never lands mid-list.
 */

import { nextTick, onBeforeUnmount, onMounted, type Ref } from 'vue'
import type { Router } from 'vue-router'

const saved = new Map<string, number>()
let popping = false
let lastWasPop = false
let lastPosition = -1
/** The entry the last navigation left. */
let fromPosition = -1

const position = (): number => {
  const p = (history.state as { position?: unknown } | null)?.position
  return typeof p === 'number' ? p : 0
}
const keyOf = (name: string) => `${position()}:${name}`

/**
 * Once, from main.ts, before the app takes the router: learn which
 * navigations are back/forward. Not from window's popstate: the router
 * handles that first, and its navigation can finish before a later listener
 * runs. The router's history tells its own listeners first, in the order
 * they listen, and this one is registered before the router's.
 */
export function installScrollMemory(router: Router) {
  router.options.history.listen((_to, _from, info) => {
    popping = info.type === 'pop'
  })
  router.afterEach(() => {
    lastWasPop = popping
    popping = false
    const p = position()
    fromPosition = lastPosition
    // A new entry reuses the position of one a back abandoned: forget it.
    if (!lastWasPop && p !== lastPosition) {
      for (const k of [...saved.keys()]) if (k.startsWith(`${p}:`)) saved.delete(k)
    }
    lastPosition = p
  })
}

/** Whether the navigation that showed this page was a back or forward. */
export function returning(): boolean {
  return lastWasPop
}

/** How long to wait for a list's rows before giving up on its offset. */
const WAIT_MS = 2000

/**
 * Keep `el`'s scroll offset under `name` and put it back on a back or
 * forward, whether the page was unmounted in between (mounted again) or only
 * hidden beside a detail pane (phones show one pane at a time).
 *
 * The offset is taken on scroll, and again on every navigation for the entry
 * being left: the list is still on screen then (this hook runs before the
 * new page renders), and a scroll event is not always delivered (a page in
 * the background, a scroll set from script).
 */
export function useScrollMemory(name: string, el: Ref<HTMLElement | null>, router: Router) {
  let cancel: (() => void) | null = null
  const save = () => {
    if (el.value && !cancel) saved.set(keyOf(name), el.value.scrollTop)
  }

  /** Scroll to the entry's offset once the list is tall enough; the reader scrolling wins. */
  function restore() {
    cancel?.()
    const target = saved.get(keyOf(name))
    const box = el.value
    if (target === undefined || !box) return
    const started = Date.now()
    let done = false
    const stop = () => {
      done = true
      observer?.disconnect()
      box.removeEventListener('wheel', stop)
      box.removeEventListener('touchstart', stop)
      cancel = null
    }
    const attempt = () => {
      if (done) return
      if (box.scrollHeight - box.clientHeight >= target - 1 || Date.now() - started > WAIT_MS) {
        box.scrollTop = target
        stop()
      }
    }
    const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(attempt)
    observer?.observe(box, { childList: true, subtree: true })
    box.addEventListener('wheel', stop, { passive: true })
    box.addEventListener('touchstart', stop, { passive: true })
    cancel = stop
    attempt()
    setTimeout(attempt, WAIT_MS + 50)
  }

  // Runs after installScrollMemory's hook, so `returning()` is this navigation's.
  const off = router.afterEach(() => {
    if (el.value && !cancel && fromPosition >= 0) {
      saved.set(`${fromPosition}:${name}`, el.value.scrollTop)
    }
    if (returning()) void nextTick(restore)
    else save()
  })

  onMounted(() => {
    el.value?.addEventListener('scroll', save, { passive: true })
    if (returning()) void nextTick(restore)
  })
  onBeforeUnmount(() => {
    off()
    cancel?.()
    el.value?.removeEventListener('scroll', save)
  })
}
